// ===========================================================================
// File: client/src/scenes/loans/LoanApplicationsTable.jsx (refactor to re-use gql)
// ===========================================================================
import React, { useMemo } from "react";
import { Box, useTheme, Chip, alpha, useMediaQuery } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useQuery } from "@apollo/client";
import LoanApplicationActionsMenu from "components/LoanApplicationActionsMenu";
import { GET_LOAN_APPLICATIONS } from "../../graphql/loans";
import { formatDisplayDate } from "../../utils/date";

const LoanApplicationsTable = ({ refreshToken }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { data, loading, error, refetch } = useQuery(GET_LOAN_APPLICATIONS);

  // refetch when refreshToken changes (e.g., after submit)
  React.useEffect(() => {
    if (refreshToken !== undefined) refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  const rows = useMemo(
    () => (data?.userLoanApplications || []).map((row, index) => ({ ...row, sequence: index + 1 })),
    [data]
  );

  const columns = [
    { field: "sequence", headerName: "#", width: 70, sortable: false, filterable: false },
    { field: "loanProduct", headerName: "Product", flex: 1 },
    { field: "requestedAmount", headerName: "Requested", flex: 1 },
    { field: "calculatedInterest", headerName: "Interest", flex: 1 },
    { field: "totalRepayableAmount", headerName: "Total Repayable", flex: 1.2 },
    {
      field: "disbursementDate",
      headerName: "Disbursed On",
      flex: 1,
      valueFormatter: ({ value }) => formatDisplayDate(value),
    },
    { field: "dueDate", headerName: "Due Date", flex: 1, valueFormatter: ({ value }) => formatDisplayDate(value) },
    { field: "disbursementPhoneNumber", headerName: "Phone", flex: 1 },
    {
      field: "status",
      headerName: "Status",
      flex: 1.2,
      renderCell: ({ value }) => {
        const label = value || "Unknown";
        const normalized = String(label).toLowerCase();
        const color = normalized.includes("approv") || normalized.includes("disburs") ? "success" : normalized.includes("reject") ? "error" : "warning";
        return <Chip size="small" label={label} color={color} sx={{ fontWeight: 700 }} />;
      },
    },
    {
      field: "reasonForRejection",
      headerName: "Rejection Reason",
      flex: 1.5,
      renderCell: ({ value }) => value || "-",
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.6,
      renderCell: ({ row }) => (
        <LoanApplicationActionsMenu
          loan={row}
          canUploadDisbursement={false}
          onActionComplete={() => refetch()}
        />
      ),
    },
  ];

  if (error) return <p>Error: {error.message}</p>;

  return (
    <Box height="75vh" sx={{
      "& .MuiDataGrid-root": { border: `1px solid ${alpha(theme.palette.secondary[200], 0.18)}`, borderRadius: "12px", overflow: "hidden" },
      "& .MuiDataGrid-cell": { borderBottom: `1px solid ${alpha(theme.palette.secondary[200], 0.08)}` },
      "& .MuiDataGrid-columnHeaders": {
        backgroundColor: theme.palette.background.alt,
        color: theme.palette.secondary[100],
        borderBottom: `1px solid ${alpha(theme.palette.secondary[200], 0.12)}`,
      },
      "& .MuiDataGrid-virtualScroller": {
        backgroundColor: theme.palette.primary.light,
      },
      "& .MuiDataGrid-footerContainer": {
        backgroundColor: theme.palette.background.alt,
        color: theme.palette.secondary[100],
        borderTop: `1px solid ${alpha(theme.palette.secondary[200], 0.12)}`,
      },
      "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
        color: `${theme.palette.secondary[200]} !important`,
      },
    }}>
      <DataGrid
        loading={loading}
        rows={rows}
        getRowId={(row) => row.id}
        columns={columns}
        rowHeight={52}
        columnVisibilityModel={
          isMobile
            ? {
                calculatedInterest: false,
                disbursementDate: false,
                dueDate: false,
                disbursementPhoneNumber: false,
                reasonForRejection: false,
              }
            : undefined
        }
      />
    </Box>
  );
};

export default LoanApplicationsTable;
